import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import paymentporterService from '../services/paymentporterService';
import profileAssignmentService from '../services/profileAssignmentService';
import routingService from '../services/routingService';
import ppProcessorService from '../services/ppProcessorService';
import ConfirmModal from './shared/ConfirmModal';
import PpDryRunModal from './PpDryRunModal';
import { lineDiff } from '../utils/lineDiff';
import './PaymentPorterPage.css';
import './PaymentPorterProcessorPage.css';
import { formatDateTime, formatDateTimeBoth } from '../utils/datetime';

const PaymentPorterCriteriaDetailPage = () => {
    const navigate = useNavigate();
    const { ppId, ruleId } = useParams();
    const ruleIdNum = parseInt(ruleId);
    const { isSuperAdmin } = useAuth();
    const PAGE_SIZE = 25;

    const [rule, setRule]                     = useState(null);
    const [pp, setPp]                         = useState(null);
    const [profileAssignments, setProfileAssignments] = useState([]);
    const [routingCodes, setRoutingCodes]     = useState([]);
    const [windowNumber, setWindowNumber]     = useState(null);
    const [matchData, setMatchData]           = useState(null); // null | 'loading' | { matched:[], matchedCount, totalReservations, businessDate, hasCriteria, queries, error? }
    const [page, setPage]                     = useState(0);    // 0-based page index (PAGE_SIZE per page)
    const [pageDetails, setPageDetails]       = useState({});   // { [reservationId]: { alreadyProcessed, processedAt } } (filled per page)
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [openRows, setOpenRows]             = useState({});   // superadmin per-row OHIP query/body inspector
    const [triggering, setTriggering]         = useState(false);
    const [triggerError, setTriggerError]     = useState(null);
    const [runningRow, setRunningRow]         = useState(null);   // reservationId being queued
    const [rowResults, setRowResults]         = useState({});     // { [reservationId]: 'inflight' | 'error:<msg>' }
    const [confirmModal, setConfirmModal]     = useState(null);   // { title, message, confirmLabel, danger, onConfirm }
    // Dry Run is synchronous — no queue, no polling, nothing written to the PMS or OpenSearch.
    const [dryRun, setDryRun]                 = useState(null);   // { reservationId, label, loading, error, result }

    useEffect(() => {
        const loadMeta = async () => {
            try {
                const [ppRes, rulesRes, assignRes, routingRes] = await Promise.all([
                    paymentporterService.getById(ppId),
                    paymentporterService.listRules(ppId),
                    profileAssignmentService.list(ppId, ruleIdNum),
                    routingService.list(ppId, ruleIdNum),
                ]);
                const ppData   = ppRes.data?.paymentPorter || null;
                const foundRule = (rulesRes.data?.rules || []).find(r => r.id === ruleIdNum) || null;
                setPp(ppData);
                setRule(foundRule);
                setWindowNumber(foundRule?.window_number ?? null);
                setProfileAssignments(assignRes.data?.profileAssignments || []);
                setRoutingCodes(routingRes.data?.routes || []);
            } catch { /* non-fatal — header degrades gracefully */ }
        };

        const loadMatchList = async () => {
            setMatchData('loading');
            setPage(0);
            setPageDetails({});
            try {
                const res = await ppProcessorService.getMatchList(ppId, ruleIdNum);
                setMatchData(res.data || { matched: [], matchedCount: 0, totalReservations: 0 });
            } catch {
                setMatchData({ matched: [], error: 'Failed to load matched reservations' });
            }
        };

        loadMeta();
        loadMatchList();
    }, [ppId, ruleId]); // eslint-disable-line

    // Data available before the render block (needed by the per-page detail effect below).
    const isLoading    = matchData === null || matchData === 'loading';
    const hasError     = !isLoading && !!matchData.error;
    const allMatched   = (!isLoading && !hasError) ? (matchData.matched || []) : [];
    const pageStart    = page * PAGE_SIZE;
    const pageSlice    = allMatched.slice(pageStart, pageStart + PAGE_SIZE);

    // Fetch the "already processed" status only for the reservations on the current page (one OHIP
    // detail call each, batched server-side). Runs whenever the page (or the underlying data) changes.
    useEffect(() => {
        const ids = pageSlice.map(r => r.reservationId).filter(Boolean);
        const missing = ids.filter(id => !(id in pageDetails));
        if (missing.length === 0) return;
        let cancelled = false;
        setDetailsLoading(true);
        ppProcessorService.getReservationDetails(ppId, missing)
            .then(res => { if (!cancelled) setPageDetails(prev => ({ ...prev, ...(res.data?.details || {}) })); })
            .catch(() => { /* leave undefined — row shows "checking…" */ })
            .finally(() => { if (!cancelled) setDetailsLoading(false); });
        return () => { cancelled = true; };
    }, [page, matchData]); // eslint-disable-line

    // Re-fetch one reservation's detail (status + before/after) and merge it into pageDetails.
    const refetchDetail = (rid) =>
        ppProcessorService.getReservationDetails(ppId, [rid])
            .then(res => { setPageDetails(prev => ({ ...prev, ...(res.data?.details || {}) })); return res.data?.details?.[rid] || null; })
            .catch(() => null);

    // Run Now is async — the Step Function writes the outcome a few seconds later. Refresh this
    // reservation's detail until a NEW run record lands. We key off lastRunAt (the record's updated_at):
    // a reservation that was ALREADY skipped/errored would otherwise match its stale terminal status on
    // the very first poll and stop before the new run finished.
    const pollAfterRun = async (rid, prevRunAt) => {
        for (let attempt = 0; attempt < 15; attempt++) {
            await new Promise(res => setTimeout(res, 4000));
            const d = await refetchDetail(rid);
            if (d && d.lastRunAt && d.lastRunAt !== prevRunAt) return true; // a fresh run record landed
        }
        return false;
    };

    // Run only the reservations shown on the current page that are eligible (not already processed,
    // skipped, or errored). Each is queued individually — clear, page-scoped, and matches the pagination.
    const handleRunPage = (targets) => {
        const list = (targets || []).filter(Boolean);
        if (!list.length) return;
        setConfirmModal({
            title: 'Run reservations on this page',
            message: `Process the ${list.length} reservation${list.length === 1 ? '' : 's'} shown on this page now? This applies the rule's actions live in the PMS. (Already-processed, skipped, and errored reservations on the page are not included.)`,
            confirmLabel: `Run ${list.length} now`,
            onConfirm: async () => {
                setTriggering(true);
                setTriggerError(null);
                try {
                    for (const r of list) {
                        const prevRunAt = r.lastRunAt || null;
                        setRowResults(prev => ({ ...prev, [r.reservationId]: 'inflight' }));
                        try {
                            await ppProcessorService.runSingleReservation(ppId, {
                                reservationId:      r.reservationId,
                                confirmationNumber: r.confirmationNum,
                                ruleId:             ruleIdNum,
                                matchedCriteria:    r.matchedCriteria || [],
                            });
                            // Each row stays "In flight" and refreshes independently until its run lands.
                            pollAfterRun(r.reservationId, prevRunAt).then(() =>
                                setRowResults(prev => { const next = { ...prev }; delete next[r.reservationId]; return next; }));
                        } catch (err) {
                            const msg = err.response?.data?.error?.message || err.response?.data?.message || 'Failed';
                            setRowResults(prev => ({ ...prev, [r.reservationId]: `error:${msg}` }));
                        }
                    }
                } finally {
                    setRunningRow(null);
                    setTriggering(false);
                }
            },
        });
    };

    // Dry Run — ask the API what it WOULD send to OHIP and show it. No confirm step: it changes nothing.
    const handleDryRun = async (r) => {
        const label = r.confirmationNum || r.reservationId;
        setDryRun({ reservationId: r.reservationId, label, loading: true, error: null, result: null });
        try {
            const res = await ppProcessorService.dryRunReservation(ppId, {
                reservationId: r.reservationId,
                ruleId:        ruleIdNum,
            });
            setDryRun({ reservationId: r.reservationId, label, loading: false, error: null, result: res.data || null });
        } catch (err) {
            const msg = err.response?.data?.error?.message || err.response?.data?.message || 'Dry run failed';
            setDryRun({ reservationId: r.reservationId, label, loading: false, error: msg, result: null });
        }
    };

    const handleRunSingle = (r) => {
        setConfirmModal({
            title: 'Run Reservation Now',
            message: `Process reservation ${r.confirmationNum || r.reservationId} now? This will apply the rule actions live in the PMS.`,
            confirmLabel: 'Run Now',
            onConfirm: async () => {
                const prevRunAt = r.lastRunAt || null;
                setRunningRow(r.reservationId);
                setRowResults(prev => ({ ...prev, [r.reservationId]: 'inflight' }));
                try {
                    await ppProcessorService.runSingleReservation(ppId, {
                        reservationId:      r.reservationId,
                        confirmationNumber: r.confirmationNum,
                        ruleId:             ruleIdNum,
                        matchedCriteria:    r.matchedCriteria || [],
                    });
                    // Stay "In flight" and keep refreshing until a new run record lands, then show it.
                    await pollAfterRun(r.reservationId, prevRunAt);
                    setRowResults(prev => { const next = { ...prev }; delete next[r.reservationId]; return next; });
                } catch (err) {
                    const msg = err.response?.data?.error?.message || err.response?.data?.message || 'Failed';
                    setRowResults(prev => ({ ...prev, [r.reservationId]: `error:${msg}` }));
                } finally {
                    setRunningRow(null);
                }
            },
        });
    };

    // Reported by criteria-preview so the screen never implies an inactive rule is live. undefined until
    // the first response lands — only an explicit false renders the banner.
    const ruleActive               = (!isLoading && !hasError) ? matchData.ruleActive : undefined;
    const businessDate             = (!isLoading && !hasError) ? matchData.businessDate : null;
    const queries                  = (!isLoading && !hasError) ? matchData.queries : null;
    const matchedCount             = (!isLoading && !hasError) ? (matchData.matchedCount ?? allMatched.length) : 0;
    const totalReservations        = (!isLoading && !hasError) ? matchData.totalReservations : null;
    const totalReservationsHasMore = (!isLoading && !hasError) ? !!matchData.totalReservationsHasMore : false;
    const totalPages               = Math.max(1, Math.ceil(matchedCount / PAGE_SIZE));

    // Merge the current page's matched rows with their fetched status (OHIP marker + latest PP outcome).
    const rows          = pageSlice.map(r => ({ ...r, ...(pageDetails[r.reservationId] || {}) }));
    const willRunRows   = rows.filter(r => pageDetails[r.reservationId] && !r.alreadyProcessed && !r.noVirtualCard && r.lastStatus !== 'SKIPPED' && r.lastStatus !== 'ERROR');
    const willRunOnPage = willRunRows.length;

    const activeRoutingCodes = routingCodes.filter(r => r.active !== false);

    return (
        <div className="pp-page" style={{ maxWidth: 1200 }}>
            <button className="pp-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}/rules/${ruleId}`)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {rule?.name || 'Rule'}
            </button>

            <div className="pp-header" style={{ marginBottom: '1.5rem' }}>
                <h1 className="pp-title">
                    Reservations Matching Criteria
                    {!isLoading && !hasError && (
                        <span className="pp-matched-count">{matchedCount} matched</span>
                    )}
                </h1>
                {/* An INACTIVE rule is still fully testable here — that is the point of this screen: prove
                    the rule with Dry Run / Run Now, then activate it. The badge says which state it is in
                    so "it matched but nothing runs on schedule" is never a surprise. */}
                {ruleActive === false && (
                    <div className="pp-rule-inactive-banner">
                        <strong>Rule is inactive</strong> — it will not run on the schedule. Dry Run and Run Now
                        still work here so you can prove it before switching it on.
                    </div>
                )}
                <p className="pp-subtitle-text">
                    {rule?.name || `Rule ${ruleId}`}{pp?.name ? ` · ${pp.name}` : ''}
                    {businessDate ? ` · Today: ${businessDate}` : ''}
                    {!isLoading && !hasError && totalReservations != null
                        ? ` · ${matchedCount} matched of ${totalReservations}${totalReservationsHasMore ? '+' : ''} reservations today`
                        : ''}
                </p>
            </div>

            {isSuperAdmin && !isLoading && !hasError && queries && (
                <div className="pp-debug-queries-section sa-only-panel">
                    <div className="pp-debug-queries-title">Debug — OHIP Queries</div>
                    {queries.step1_list?.length > 0 && (
                        <>
                            <div className="pp-debug-queries-label">Step 1 · List calls ({queries.step1_list.length})</div>
                            {queries.step1_list.map((q, i) => (
                                <div key={i} className="pp-debug-query-row">
                                    {q.axis && <span className="pp-debug-query-axis">{q.axis}</span>}
                                    <span className="pp-debug-query-url">{q.url}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}

            {isLoading && (
                <div className="ppp-loading">Fetching from OHIP…</div>
            )}

            {hasError && (
                <div className="ppp-error">{matchData.error}</div>
            )}

            {!isLoading && !hasError && matchedCount === 0 && (
                <div className="ppp-empty">No reservations matching criteria found for today.</div>
            )}

            {!isLoading && !hasError && matchedCount > 0 && (
              <>
                <div className="pp-pagination-bar">
                    <span className="pp-pagination-info">
                        Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, matchedCount)} of {matchedCount} matched
                        {detailsLoading
                            ? ' · checking this page…'
                            : ` · ${willRunOnPage} of these ${rows.length} will run`}
                    </span>
                    <span className="pp-pagination-controls">
                        <button className="pp-page-btn" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Prev</button>
                        <span className="pp-page-indicator">Page {page + 1} of {totalPages}</span>
                        <button className="pp-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Next →</button>
                    </span>
                </div>
                <div className="ppp-table-wrap">
                    <table className="ppp-table">
                        <thead>
                            <tr>
                                <th>Reservation ID</th>
                                <th>Confirmation</th>
                                <th>Guest</th>
                                <th>Arrival</th>
                                <th>Departure</th>
                                <th>Matched Criteria</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const isRunning  = runningRow === r.reservationId;
                                const rowResult  = rowResults[r.reservationId];
                                const isQueued   = rowResult === 'inflight';
                                const rowError   = typeof rowResult === 'string' && rowResult.startsWith('error:') ? rowResult.slice(6) : null;
                                const checked    = pageDetails[r.reservationId] !== undefined;
                                const isOpen     = !!openRows[r.reservationId];
                                return (
                                  <React.Fragment key={r.reservationId}>
                                    <tr className={r.alreadyProcessed ? 'pp-modal-row-processed' : ''}>
                                        <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.reservationId || '—'}</td>
                                        <td>{r.confirmationNum || '—'}</td>
                                        <td>{r.guestName || '—'}</td>
                                        <td>{r.arrivalDate || '—'}</td>
                                        <td>{r.departureDate || '—'}</td>
                                        <td>
                                            <div className="pp-modal-criteria-list">
                                                {(r.matchedCriteria || []).map((c, i) => (
                                                    <span key={i} className="pp-modal-criteria-tag">{c}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td>
                                            {rowResult === 'inflight'
                                                ? <span className="pp-modal-pending-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#fef3c7', color: '#b45309' }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
                                                    In flight
                                                  </span>
                                                : !checked
                                                ? <span className="pp-modal-checking-badge">Checking…</span>
                                                : r.alreadyProcessed
                                                    ? <span className="pp-modal-processed-badge" title={r.processedAt ? `Processed ${r.processedAt}` : undefined}>Already processed</span>
                                                    : r.noVirtualCard
                                                        ? <span className="pp-modal-error-badge" title="No virtual card on reservation — cannot move card">Error — no virtual card</span>
                                                        : r.lastStatus === 'ERROR'
                                                            ? <div><span className="pp-modal-error-badge">Error</span>{r.lastError && <div style={{ fontSize: '0.72rem', color: '#b91c1c', marginTop: '0.3rem', maxWidth: 320, whiteSpace: 'normal' }}>{r.lastError}</div>}</div>
                                                            : r.lastStatus === 'SKIPPED'
                                                                ? <div><span className="pp-modal-skipped-badge">Skipped</span>{r.lastSkipReason && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.3rem', maxWidth: 320, whiteSpace: 'normal' }}>{r.lastSkipReason}</div>}</div>
                                                                : <span className="pp-modal-pending-badge">Will run</span>
                                            }
                                            {r.lastRunAt && (
                                                <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '0.35rem' }}
                                                     title={formatDateTimeBoth(r.lastRunAt)}>
                                                    Last evaluated {formatDateTime(r.lastRunAt)}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="pp-run-cell">
                                                <button
                                                    className={`ppp-detail-btn${isQueued ? ' ppp-detail-btn-success' : rowError ? ' ppp-detail-btn-error' : ' ppp-detail-btn-run'}`}
                                                    onClick={() => handleRunSingle(r)}
                                                    disabled={isRunning || isQueued}
                                                    title={rowError || undefined}
                                                >
                                                    {isRunning
                                                        ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg></>
                                                        : isQueued ? <><svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{flexShrink:0}}><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>Queued</>
                                                        : rowError ? 'Failed'
                                                        : <><svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{flexShrink:0}}><polygon points="1,0.5 9.5,5 1,9.5"/></svg>Run Now</>
                                                    }
                                                </button>
                                                {/* Beside Run Now: same target reservation, but returns the
                                                    payload instead of applying it. Always enabled — a dry
                                                    run is safe even for already-processed rows, and seeing
                                                    WHY one would skip is the point. */}
                                                <button
                                                    className="ppp-detail-btn"
                                                    onClick={() => handleDryRun(r)}
                                                    disabled={dryRun?.loading}
                                                    title="Show the OHIP payload this reservation would produce — nothing is sent"
                                                >
                                                    {dryRun?.loading && dryRun.reservationId === r.reservationId
                                                        ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg></>
                                                        : 'Dry Run'}
                                                </button>
                                                {isSuperAdmin && checked && (
                                                    <button
                                                        className={`pp-row-chevron sa-only${isOpen ? ' open' : ''}`}
                                                        title="Show the OHIP reservation query, response & before/after diff"
                                                        onClick={() => {
                                                            const willOpen = !openRows[r.reservationId];
                                                            setOpenRows(prev => ({ ...prev, [r.reservationId]: willOpen }));
                                                            if (willOpen) {
                                                                // Re-fetch this row so the panel reflects the latest run result (before/after, status).
                                                                ppProcessorService.getReservationDetails(ppId, [r.reservationId])
                                                                    .then(res => setPageDetails(prev => ({ ...prev, ...(res.data?.details || {}) })))
                                                                    .catch(() => {});
                                                            }
                                                        }}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                    </button>
                                                )}
                                            </div>
                                            {rowError && <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem', maxWidth: 120 }}>{rowError}</div>}
                                        </td>
                                    </tr>
                                    {isSuperAdmin && isOpen && (
                                        <tr className="pp-detail-expand-row">
                                            <td colSpan={8} className="pp-detail-expand-cell">
                                                <div className="pp-detail-toolbar">
                                                    {r.query
                                                        ? <div className="pp-detail-query"><span className="pp-detail-method">{r.query.method}</span> <span className="pp-detail-url">{r.query.url}</span></div>
                                                        : <div className="pp-detail-query">(query unavailable)</div>}
                                                    <button className="pp-detail-refresh" onClick={() => refetchDetail(r.reservationId)} title="Re-fetch the run result">↻ Refresh</button>
                                                </div>
                                                {r.lastStatus === 'ERROR' && (
                                                    <div className="pp-detail-error">Run failed: {r.lastError || 'Unknown error'}</div>
                                                )}
                                                {(r.before && r.after) ? (
                                                    <>
                                                        <div className="pp-detail-body-label">Before → After — changes the run applied</div>
                                                        <pre className="pp-detail-body pp-detail-diff">
                                                            {lineDiff(JSON.stringify(r.before, null, 2), JSON.stringify(r.after, null, 2)).map((l, i) => (
                                                                <div key={i} className={l.t === 'add' ? 'pp-diff-add' : l.t === 'del' ? 'pp-diff-del' : 'pp-diff-same'}>
                                                                    <span className="pp-diff-gutter">{l.t === 'add' ? '+' : l.t === 'del' ? '-' : ' '}</span>{l.s}
                                                                </div>
                                                            ))}
                                                        </pre>
                                                    </>
                                                ) : r.before ? (
                                                    <>
                                                        <div className="pp-detail-body-label">{
                                                            r.lastStatus === 'ERROR'
                                                                ? 'Reservation at run time (not modified — run errored before any update)'
                                                                : (r.alreadyProcessed || r.lastStatus === 'SUCCESS')
                                                                    ? 'Reservation (already processed — this is the current, ported state)'
                                                                    : 'Reservation at run time (not modified — no change made)'
                                                        }</div>
                                                        <pre className="pp-detail-body">{JSON.stringify(r.before, null, 2)}</pre>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="pp-detail-body-label">{
                                                            r.alreadyProcessed
                                                                ? 'Current reservation (already processed)'
                                                                : `Response body${!r.lastStatus ? ' (not run yet — before/after appears after a run)' : ''}`
                                                        }</div>
                                                        <pre className="pp-detail-body">{r.body ? JSON.stringify(r.body, null, 2) : '(no response body)'}</pre>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                  </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
              </>
            )}

            {!isLoading && !hasError && (
                <div className="pp-criteria-detail-run-section">
                    <div className="pp-run-warning">
                        Runs the <strong>{willRunOnPage} eligible reservation{willRunOnPage === 1 ? '' : 's'} on this page</strong> (page {page + 1} of {totalPages}) — not the whole matched set — and applies:
                        {(windowNumber != null || profileAssignments.length > 0 || activeRoutingCodes.length > 0) ? (
                            <ul className="pp-run-warning-list">
                                {windowNumber != null && <li>Move reservation to <strong>Window {windowNumber}</strong></li>}
                                {profileAssignments.map(a => (
                                    <li key={a.id}>Assign profile <strong>{a.profile_name || a.profile_id}</strong>{a.profile_type ? ` (${a.profile_type})` : ''}</li>
                                ))}
                                {activeRoutingCodes.map(r => (
                                    <li key={r.id}>Assign routing code <strong>{r.code}</strong>{r.description ? ` — ${r.description}` : ''}</li>
                                ))}
                            </ul>
                        ) : (
                            <span className="pp-run-warning-none"> No actions configured — reservations will be qualified but no PMS changes will be made.</span>
                        )}
                        <div className="pp-run-warning-note">Paginate and run each page to process more. Already-processed, skipped, and errored rows are excluded.</div>
                    </div>
                    <button className="pp-run-btn pp-run-btn-primary" onClick={() => handleRunPage(willRunRows)} disabled={triggering || willRunOnPage === 0}>
                        {triggering
                            ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Running…</>
                            : willRunOnPage === 0
                                ? <>Nothing to run on this page</>
                                : <><svg width="12" height="12" viewBox="0 0 10 10" fill="currentColor" style={{flexShrink:0}}><polygon points="1,0.5 9.5,5 1,9.5"/></svg>Run {willRunOnPage} reservation{willRunOnPage === 1 ? '' : 's'} on this page</>
                        }
                    </button>
                    {triggerError && <div className="pp-run-error">{triggerError}</div>}
                </div>
            )}

            {dryRun && (
                <PpDryRunModal
                    reservationLabel={dryRun.label}
                    loading={dryRun.loading}
                    error={dryRun.error}
                    result={dryRun.result}
                    onClose={() => setDryRun(null)}
                />
            )}

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    danger={confirmModal.danger}
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default PaymentPorterCriteriaDetailPage;
